import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Clock, User, Car, AlertTriangle, CheckCircle, CheckCheck, Play, Pause,
    Wrench, Plus, Search, RefreshCw, MessageSquare,
    X, UserPlus, Tag, Pencil, Activity,
    LayoutPanelLeft, Send,
    Filter, MoreHorizontal, MapPin, Users, Package, Boxes, Trash2,
} from 'lucide-react';
import Layout from './Layout';
import { fetchStaffList } from '../store/slices/staffSlice';
import {
    fetchJobs,
    fetchJob,
    fetchJobSummary,
    fetchWorkCategories,
    fetchCategoryTypes,
    startJob,
    pauseJob,
    resumeJob,
    completeJob,
    cancelJob,
    purgeJob,
    vehicleCollected,
    assignStaff,
    unassignStaff,
    addJobNote,
    openViewModal,
    closeViewModal,
    openAssignModal,
    closeAssignModal,
    openCancelConfirm,
    closeCancelConfirm,
    openCompleteConfirm,
    closeCompleteConfirm,
    openVehicleCollectedConfirm,
    closeVehicleCollectedConfirm,
    clearOperationStatus,
    setOperationSuccess,
    selectJob,
    clearSelectedJob,
    fetchJobResources,
    fetchAvailableResources,
    assignResource,
    unassignResource,
    addWorkItem,
    removeWorkItem,
    updateWorkItem,
    startAllWorkItems,
    completeAllWorkItems,
} from '../store/slices/jobSlice';
import JobNotesTab from './JobNotesTab';
import JobDetailModal, { WorkItemCard, AddWorkItemModal, JobApprovalCard, BatchApprovalCard, ApprovalHistoryList } from './JobDetailModal';
import LoanCarReturnModal from './LoanCarReturnModal';
import { getLoanCarHandoverStatusApi } from '../services/loanCarIncidentApi';
import JobsToolbar from './JobsToolbar';
import { currentMonthKey } from '../utils/monthKey';
import { jobsBoardQuery } from '../utils/jobsBoardQuery';
import { useIsTablet } from './useIsTablet';
import useQueuedJobs from '../hooks/useQueuedJobs';
import PagedSearchControls from './PagedSearchControls';
import { useServerSearchTerm } from '../hooks/useSearchBox';
import LaneLoading from './LaneLoading';
import { filterByNameOrRego } from '../utils/localSearch';
import { QUEUED_COLUMN_STATUSES, isQueuedStatus } from '../utils/queuedStatuses';

// ─────────────────────────────────────────────────────────────────────────────
// Re-used pure helpers (identical to JobsPage — no logic changed)
// ─────────────────────────────────────────────────────────────────────────────

const getLiveActiveSeconds = (job) => {
    if (!job.actualStartDate) return 0;
    const base = job.activeSeconds ?? 0;
    if (job.status === 'IN_PROGRESS' && job.updatedAt) {
        const staleness = (Date.now() - new Date(job.updatedAt).getTime()) / 1000;
        return base + Math.max(0, staleness);
    }
    return base;
};

const getSloHours = (job) => {
    if (job.sloBudgetSeconds != null && job.sloBudgetSeconds > 0) return job.sloBudgetSeconds / 3600;
    return null;
};

const getSloStatus = (activeSeconds, sloBudgetSeconds) => {
    if (!sloBudgetSeconds) return 'green';
    const pct = (activeSeconds / sloBudgetSeconds) * 100;
    if (pct >= 100) return 'red';
    if (pct >= 75)  return 'amber';
    return 'green';
};

const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
};

const getLivePausedSeconds = (job) => {
    if (!job.actualStartDate) return 0;
    let paused = job.pausedDurationSeconds ?? 0;
    if (job.status === 'PAUSED' && job.updatedAt) {
        const currentPauseSeconds = (Date.now() - new Date(job.updatedAt).getTime()) / 1000;
        paused += Math.max(0, currentPauseSeconds);
    }
    return paused;
};

// Backend serialises vehicleDescription as `${year} ${make} ${model}`, which
// surfaces a stray leading "0 " when the year is null/0 (e.g. "0 Honda Civic").
// Strip the placeholder so the header reads cleanly. Also handles the case
// where year is the literal string "0" or "0000".
const formatVehicleDescription = (desc) => {
    if (!desc) return desc;
    return desc.replace(/^0+\s+/, '').trim();
};



// ─────────────────────────────────────────────────────────────────────────────
// iPad detection — extracted to its own file (./useIsTablet) so this module
// stays a Fast-Refresh-clean "only React components" file. See useIsTablet.js
// for the implementation.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// Keyframes (same heartbeat + toast animations as JobsPage)
// ─────────────────────────────────────────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('wdk-tablet-keyframes')) {
    const s = document.createElement('style');
    s.id = 'wdk-tablet-keyframes';
    s.textContent = `
        @keyframes wdk-heartbeat {
            0%   { box-shadow: 0 0 0 0 rgba(220,38,38,.55); }
            40%  { box-shadow: 0 0 0 8px rgba(220,38,38,0); }
            100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
        }
        .wdk-overdue { animation: wdk-heartbeat 1.6s ease-in-out infinite; border-color: rgb(220 38 38) !important; }
        @keyframes wdk-toast-in  { from{transform:translateX(110%);opacity:0} to{transform:translateX(0);opacity:1} }
        @keyframes wdk-toast-out { from{transform:translateX(0);opacity:1}   to{transform:translateX(110%);opacity:0} }
        @keyframes wdk-toast-prog { from{width:100%} to{width:0%} }
        .wdk-toast-enter { animation: wdk-toast-in .35s cubic-bezier(.34,1.56,.64,1) forwards; }
        .wdk-toast-exit  { animation: wdk-toast-out .3s ease-in forwards; }
        .wdk-toast-prog  { animation: wdk-toast-prog linear forwards; }
    `;
    document.head.appendChild(s);
}

// ─────────────────────────────────────────────────────────────────────────────
// Status helpers
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_META = {
    CREATED:    { label: 'Queued',      dot: 'bg-gray-400',    pill: 'bg-gray-100 text-gray-700' },
    QUOTED:     { label: 'Quoted',      dot: 'bg-gray-400',    pill: 'bg-gray-100 text-gray-700' },
    APPROVED:   { label: 'Approved',    dot: 'bg-gray-400',    pill: 'bg-gray-100 text-gray-700' },
    IN_PROGRESS:{ label: 'In progress', dot: 'bg-blue-500',    pill: 'bg-blue-50 text-blue-700' },
    PAUSED:     { label: 'Paused',      dot: 'bg-amber-400',   pill: 'bg-amber-50 text-amber-700' },
    COMPLETED:  { label: 'Completed',   dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700' },
    CANCELLED:  { label: 'Cancelled',   dot: 'bg-red-400',     pill: 'bg-red-50 text-red-700' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Toast (UI-only re-implementation, same behaviour)
// ─────────────────────────────────────────────────────────────────────────────

const TOAST_DURATION = 5000;

const Toast = ({ message, type, onDismiss }) => {
    const [exiting, setExiting] = useState(false);
    const isSuccess = type === 'success';

    const dismiss = useCallback(() => {
        setExiting(true);
        setTimeout(onDismiss, 300);
    }, [onDismiss]);

    useEffect(() => {
        const t = setTimeout(dismiss, TOAST_DURATION);
        return () => clearTimeout(t);
    }, [dismiss]);

    return (
        <div className={`${exiting ? 'wdk-toast-exit' : 'wdk-toast-enter'} fixed top-4 right-4 z-[100] flex flex-col overflow-hidden rounded-xl shadow-xl min-w-[280px] max-w-sm`}>
            <div className={`flex items-start gap-3 px-4 py-3 ${isSuccess ? 'bg-emerald-100 border border-emerald-200' : 'bg-red-100 border border-red-200'}`}>
                <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${isSuccess ? 'bg-emerald-200' : 'bg-red-200'}`}>
                    {isSuccess
                        ? <CheckCircle size={14} className="text-emerald-700" />
                        : <AlertTriangle size={14} className="text-red-700" />}
                </div>
                <p className="flex-1 text-sm text-black leading-snug pt-0.5 font-medium">{message}</p>
                <button onClick={dismiss} className={`shrink-0 p-1 rounded-lg ${isSuccess ? 'text-emerald-600 hover:bg-emerald-200' : 'text-red-600 hover:bg-red-200'}`}>
                    <X size={13} />
                </button>
            </div>
            <div className={`h-1 w-full ${isSuccess ? 'bg-emerald-200' : 'bg-red-200'}`}>
                <div className={`h-full wdk-toast-prog ${isSuccess ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ animationDuration: `${TOAST_DURATION}ms` }} />
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SidebarJobRow — one row in the left-panel job list
// ─────────────────────────────────────────────────────────────────────────────

const SidebarJobRow = ({ job, isSelected, onClick, currentTime }) => {
    const activeSeconds = getLiveActiveSeconds(job);
    const sloStatus     = (job.status === 'IN_PROGRESS' || job.status === 'PAUSED')
        ? getSloStatus(activeSeconds, job.sloBudgetSeconds ?? null)
        : 'green';
    const isOverdue  = job.overdue === true || sloStatus === 'red';
    const isAwait    = job.status === 'PAUSED' && job.hasAwaitingConfirmation === true;
    const meta       = STATUS_META[job.status] || STATUS_META.CREATED;

    const borderColor = isSelected
        ? 'border-l-violet-500'
        : isOverdue
            ? 'border-l-red-500'
            : isAwait
                ? 'border-l-yellow-400'
                : sloStatus === 'amber'
                    ? 'border-l-amber-400'
                    : 'border-l-transparent';

    const pendingMeasurements = job.pendingObservationCount ?? 0;

    return (
        <button
            onClick={onClick}
            className={`w-full text-left px-3 py-3 border-l-4 border-b border-gray-100 transition-all duration-150
                ${borderColor}
                ${isSelected ? 'bg-violet-50' : 'bg-white hover:bg-gray-50'}
                ${isOverdue && !isSelected ? 'wdk-overdue' : ''}`}
        >
            <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                    <span className="text-sm font-semibold text-gray-900 truncate">{job.customerName}</span>
                </div>
                {pendingMeasurements > 0 && (
                    <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        {pendingMeasurements} meas.
                    </span>
                )}
                {job.priority === 'URGENT' && (
                    <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-0.5">
                        <AlertTriangle size={9} />URGENT
                    </span>
                )}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-1 ml-3.5">
                <Car size={10} className="shrink-0" />
                <span className="truncate">{formatVehicleDescription(job.vehicleDescription) || job.vehicleRego}</span>
            </div>
            <div className="flex items-center justify-between ml-3.5">
                <span className="text-[10px] text-gray-400 font-mono">{job.jobNumber}</span>
                {activeSeconds > 0 && (
                    <span className={`text-[10px] font-mono font-semibold flex items-center gap-0.5
                        ${isOverdue ? 'text-red-600' : sloStatus === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`}>
                        <Clock size={9} />
                        {formatTime(activeSeconds / 3600)}
                    </span>
                )}
            </div>
            {isAwait && (
                <div className="ml-3.5 mt-1.5 text-[10px] font-semibold text-yellow-700 flex items-center gap-1">
                    <Clock size={9} />Awaiting customer approval
                </div>
            )}
        </button>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// TabletConfirmDialog — in-app replacement for window.confirm
// Native browser dialogs look out of place on iPad and the OK/Cancel labels
// can't be customised; this matches the rest of the app's modal styling.
// ─────────────────────────────────────────────────────────────────────────────

const TabletConfirmDialog = ({
                                 open,
                                 title = 'Confirm',
                                 message,
                                 confirmLabel = 'Confirm',
                                 cancelLabel = 'Cancel',
                                 variant = 'danger',
                                 onConfirm,
                                 onCancel,
                             }) => {
    if (!open) return null;

    const variantStyles = {
        danger:  { btn: 'bg-red-600 hover:bg-red-700 text-white',       icon: 'bg-red-100 text-red-600' },
        warning: { btn: 'bg-amber-500 hover:bg-amber-600 text-white',   icon: 'bg-amber-100 text-amber-600' },
        primary: { btn: 'bg-violet-600 hover:bg-violet-700 text-white', icon: 'bg-violet-100 text-violet-600' },
    };
    const v = variantStyles[variant] || variantStyles.danger;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
            onClick={onCancel}
        >
            <div
                className="bg-white rounded-2xl w-full max-w-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="px-5 py-4 border-b flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${v.icon}`}>
                        <AlertTriangle size={18} />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                </div>
                <div className="px-5 py-4">
                    <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
                </div>
                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-2xl flex gap-2 justify-end">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        onClick={onConfirm}
                        className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${v.btn}`}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// WorkItemPanel — right-panel content for one selected job
// Uses WorkItemCard from JobDetailModal for consistent rendering and correct
// measurementObservations field access.
// ─────────────────────────────────────────────────────────────────────────────

const TERMINAL_STATUSES_TABLET = ['COMPLETED', 'INVOICED', 'VEHICLE_COLLECTED', 'CANCELLED'];

/**
 * Sum a work item's cost detail: labour value + sum of part line values.
 * Null-safe at every level. Mirrors the WorkItemCostDetail shape used by
 * the desktop JobDetailModal so the tablet list orders work items the same
 * way (priced items first, $0 inspection/check items last).
 */
const workItemTotalTablet = (item) => {
    if (!item || !item.costDetail) return 0;
    const labour = Number(item.costDetail.labour?.value) || 0;
    const parts = (item.costDetail.parts ?? []).reduce(
        (s, p) => s + (Number(p.value) || 0),
        0,
    );
    return labour + parts;
};

const WorkItemPanel = ({ job, dispatch, onAddWorkItem, onEditWorkItem, onRemoveWorkItem }) => {
    const workItems  = job.workItems ?? [];
    // Priced items (anything with a non-zero costDetail total) sort to the top.
    // $0 inspection/check items fall to the bottom. Stable sort
    // (Array.prototype.sort is stable per ES2019), so within each group the
    // backend's sortOrder is preserved. Mirrors the desktop JobDetailModal
    // ordering so all three surfaces show the same list shape.
    const sortedWorkItems = [...workItems]
        .sort((a, b) => (workItemTotalTablet(b) > 0 ? 1 : 0) - (workItemTotalTablet(a) > 0 ? 1 : 0));
    // Editability is unconditionally true on tablet/mobile to mirror the desktop
    // JobDetailModal — backend authorisation rules remain the source of truth
    // for what's actually permitted. This lets staff fix typos on completed
    // jobs, record a missed observation after the fact, or tidy up notes on a
    // vehicle that's already been collected, the same way they can on desktop.
    const isEditable         = true;
    const isWorkItemEditable = true;

    // In-app confirmation for work item removal — replaces window.confirm so
    // the prompt is styled consistently with the rest of the app and doesn't
    // pop a jarring native iPad dialog.
    const [pendingRemoveUuid, setPendingRemoveUuid] = useState(null);

    if (workItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3 py-16">
                <Wrench size={32} className="opacity-30" />
                <p className="text-sm">No work items on this job yet</p>
                {isEditable && (
                    <button
                        onClick={onAddWorkItem}
                        className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-colors"
                    >
                        <Plus size={14} /> Add work item
                    </button>
                )}
                {job.approvalMode === 'PROFILE' && (
                    <div className="w-full px-4 mt-4">
                        <JobApprovalCard job={job} isEditable={isEditable} operationLoading={false} dispatch={dispatch} />
                    </div>
                )}
            </div>
        );
    }

    // Progress bar counts — use measurementObservations (correct API field)
    const totalObs    = workItems.reduce((n, wi) => n + (wi.measurementObservations?.length ?? 0), 0);
    const recordedObs = workItems.reduce((n, wi) =>
        n + (wi.measurementObservations?.filter(o => o.value || o.observationStatus).length ?? 0), 0);

    const handleRemove = (uuid) => { setPendingRemoveUuid(uuid); };
    const handleConfirmRemove = () => {
        const uuid = pendingRemoveUuid;
        setPendingRemoveUuid(null);
        if (uuid) onRemoveWorkItem(uuid);
    };

    return (
        <div className="flex flex-col h-full">
            {/* Progress bar */}
            {totalObs > 0 && (
                <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3 bg-gray-50 shrink-0">
                    <span className="text-xs text-gray-500 shrink-0">Measurements</span>
                    <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all duration-500 ${recordedObs === totalObs ? 'bg-emerald-500' : 'bg-violet-500'}`}
                            style={{ width: totalObs > 0 ? `${Math.round((recordedObs / totalObs) * 100)}%` : '0%' }}
                        />
                    </div>
                    <span className={`text-xs font-semibold shrink-0 ${recordedObs === totalObs ? 'text-emerald-600' : 'text-violet-600'}`}>
                        {recordedObs} / {totalObs}
                    </span>
                </div>
            )}

            {/* Work item list — WorkItemCard handles measurementObservations correctly.
                The previous footer "Add work item" button has been promoted to the
                tab bar (next to the Notes tab) so it stays visible regardless of
                scroll position; the list now ends at the last work item. */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {sortedWorkItems.map((wi) => (
                    <WorkItemCard
                        key={wi.uuid}
                        item={wi}
                        job={job}
                        isEditable={isEditable}
                        isWorkItemEditable={isWorkItemEditable}
                        operationLoading={false}
                        dispatch={dispatch}
                        onEdit={() => onEditWorkItem(wi)}
                        onRemove={() => handleRemove(wi.uuid)}
                    />
                ))}
                {/* Job-level customer approval — interactive card for PROFILE mode */}
                {job.approvalMode === 'PROFILE' && (
                    <JobApprovalCard job={job} isEditable={isEditable} operationLoading={false} dispatch={dispatch} />
                )}
            </div>

            {/* In-app confirmation for removing a work item. */}
            <TabletConfirmDialog
                open={pendingRemoveUuid != null}
                title="Remove work item?"
                message="This will remove the work item from the job. This action cannot be undone."
                confirmLabel="Remove"
                cancelLabel="Cancel"
                variant="danger"
                onConfirm={handleConfirmRemove}
                onCancel={() => setPendingRemoveUuid(null)}
            />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ResourcesTabletPanel — touch-friendly assigned + available picker
// (mirrors the desktop ResourcePanel but sized for the iPad tab)
// ─────────────────────────────────────────────────────────────────────────────

const RESOURCE_ICON = (type, sz = 14) => ({
    EQUIPMENT:  <Wrench size={sz} className="text-violet-500" />,
    TOOL:       <Wrench size={sz} className="text-blue-500" />,
    SPACE:      <MapPin size={sz} className="text-emerald-500" />,
    CONSULTANT: <Users  size={sz} className="text-amber-500" />,
}[type] || <Package size={sz} className="text-gray-400" />);

const RESOURCE_BADGE = (type) => ({
    EQUIPMENT:  'bg-violet-100 text-violet-700',
    TOOL:       'bg-blue-100 text-blue-700',
    SPACE:      'bg-emerald-100 text-emerald-700',
    CONSULTANT: 'bg-amber-100 text-amber-700',
}[type] || 'bg-gray-100 text-gray-600');

const ResourcesTabletPanel = ({
                                  jobResources,
                                  availableResources,
                                  resourcesLoading,
                                  operationLoading,
                                  resourceTypeFilter,
                                  onFilterChange,
                                  onAssign,
                                  onUnassign,
                              }) => {
    const assigned = jobResources ?? [];
    const available = availableResources ?? [];

    return (
        <div className="space-y-5">
            {/* Assigned */}
            <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Assigned {assigned.length > 0 && `(${assigned.length})`}
                </p>
                {assigned.length === 0
                    ? <p className="text-sm text-gray-400 italic">No resources allocated</p>
                    : (
                        <div className="space-y-2">
                            {assigned.map(r => (
                                <div key={r.resourceUuid} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {RESOURCE_ICON(r.resourceType, 14)}
                                        <span className="text-sm font-medium text-gray-900 truncate">{r.name}</span>
                                        {r.resourceIdentifier && (
                                            <span className="text-xs text-gray-400 font-mono shrink-0">{r.resourceIdentifier}</span>
                                        )}
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${RESOURCE_BADGE(r.resourceType)}`}>
                                            {r.resourceType?.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => onUnassign(r)}
                                        disabled={operationLoading}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0 disabled:opacity-50"
                                        title="Release"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )
                }
            </div>

            {/* Available */}
            <div className="border rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b flex-wrap">
                    <Boxes size={14} className="text-gray-400 shrink-0" />
                    <span className="text-xs font-medium text-gray-500 mr-1">Available:</span>
                    {['', 'SPACE', 'CONSULTANT'].map(t => (
                        <button
                            key={t}
                            onClick={() => onFilterChange(t)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                resourceTypeFilter === t
                                    ? 'bg-violet-600 text-white'
                                    : 'bg-white border text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {t === '' ? 'All' : t.charAt(0) + t.slice(1).toLowerCase().replace('_', ' ')}
                        </button>
                    ))}
                    {resourcesLoading && <RefreshCw size={12} className="animate-spin text-gray-400 ml-auto" />}
                </div>
                <div className="max-h-72 overflow-y-auto">
                    {resourcesLoading && available.length === 0
                        ? <div className="py-6 text-center text-sm text-gray-400"><RefreshCw size={16} className="animate-spin mx-auto mb-1" /></div>
                        : available.length === 0
                            ? <div className="py-5 text-center text-sm text-gray-400">No available resources{resourceTypeFilter ? ` of type ${resourceTypeFilter.toLowerCase()}` : ''}</div>
                            : (
                                <div className="divide-y">
                                    {available.map(r => (
                                        <button
                                            key={r.uuid}
                                            onClick={() => onAssign(r)}
                                            disabled={operationLoading}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-violet-50 transition-colors disabled:opacity-50"
                                        >
                                            {RESOURCE_ICON(r.resourceType, 14)}
                                            <span className="text-sm font-medium text-gray-800 flex-1 truncate">{r.name}</span>
                                            {r.resourceIdentifier && (
                                                <span className="text-xs text-gray-400 font-mono shrink-0">{r.resourceIdentifier}</span>
                                            )}
                                            {r.baseCostPerDay != null && (
                                                <span className="text-xs text-gray-500 shrink-0">${Number(r.baseCostPerDay).toFixed(2)}/day</span>
                                            )}
                                            <Plus size={16} className="text-violet-500 shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            )
                    }
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// JobDetailPanel — the full right panel (header + tabs + content)
// ─────────────────────────────────────────────────────────────────────────────

const JobDetailPanel = ({
                            job,
                            dispatch,
                            operationLoading,
                            jobResources,
                            availableResources,
                            resourcesLoading,
                            workCategories,
                            categoryTypes,
                            onStart,
                            onPause,
                            onResume,
                            onOpenComplete,
                            onOpenVehicleCollect,
                            onOpenAssign,
                            onAddWorkItem,
                            onEditWorkItem,
                            onRemoveWorkItem,
                            onAddNote,
                            onRefreshJob,
                            onAssignResource,
                            onUnassignResource,
                            resourceTypeFilter,
                            onResourceTypeFilter,
                            onDelete,
                        }) => {
    const [activeTab, setActiveTab] = useState('workitems');

    // Reset to work items tab whenever a new job is selected
    useEffect(() => { setActiveTab('workitems'); }, [job.uuid]);

    const activeSeconds = getLiveActiveSeconds(job);
    const sloHours      = getSloHours(job);
    const sloStatus     = (job.status === 'IN_PROGRESS' || job.status === 'PAUSED')
        ? getSloStatus(activeSeconds, job.sloBudgetSeconds ?? null)
        : 'green';
    const isOverdue    = job.overdue === true || sloStatus === 'red';
    const meta         = STATUS_META[job.status] || STATUS_META.CREATED;

    // Tab order: Work items → Notes → Customer Approvals → Resources. Notes
    // lives next to Work items because the two are used together during a job
    // (record a measurement → jot a note), so keeping them adjacent saves taps.
    // Customer Approvals sits after Notes so the tech can review the audit
    // trail and send fresh approval requests without leaving the right panel.
    //
    // The approvals tab gets a small badge / dot to mirror the desktop modal:
    //   • dot      → an approval request is currently in flight (SENT, no response yet)
    //   • count    → either the number of work items still eligible for approval,
    //                or — when nothing is eligible — the size of the history list
    //                so the tab still reflects past activity.
    const eligibleApprovalCount = (job.workItems || []).filter(wi =>
        wi.customerConfirmed === null || wi.customerConfirmed === undefined
    ).length;
    const approvalAwaiting = job.lastApprovalStatus === 'SENT';
    const historyCount = Array.isArray(job.approvalHistory) ? job.approvalHistory.length : 0;
    const approvalsBadge = approvalAwaiting
        ? null
        : (eligibleApprovalCount > 0 ? eligibleApprovalCount : historyCount);

    const TABS = [
        { id: 'workitems', label: 'Work items' },
        // Matches the desktop modal — see JobDetailModal. The phone keeps the short
        // "Notes" because its labels are all abbreviated to fit three across.
        { id: 'notes',     label: 'Notes & Photos' },
        { id: 'approvals', label: 'Customer Approvals', count: approvalsBadge, dot: approvalAwaiting },
        { id: 'resources', label: 'Resources' },
    ];

    // Add work item button is rendered inline with the tabs (not inside the
    // panel) so it stays visible at the top of the right pane regardless of
    // which tab is active and never gets pushed off-screen when the work item
    // list is long. Matches the violet primary style used on the desktop modal.
    // Editability is unconditionally true to mirror the desktop JobDetailModal.
    // Backend authorisation rules remain the source of truth for what's actually
    // permitted; the UI no longer gates by job status.
    const isEditable         = true;
    const isWorkItemEditable = true;

    // Bulk Start All / Complete All — same behaviour as the desktop modal but
    // rendered inline with the tab row so they're reachable without scrolling
    // on iPad. Hidden on tabs other than Work items so the row stays uncluttered.
    const [bulkConfirm, setBulkConfirm] = useState(null); // 'start' | 'complete' | null
    const [bulkBusy, setBulkBusy] = useState(false);
    const pendingCount = (job.workItems || []).filter(wi => wi.status === 'PENDING').length;
    const nonTerminalCount = (job.workItems || [])
        .filter(wi => wi.status !== 'COMPLETED' && wi.status !== 'DECLINED').length;
    // Hide Start All / Complete All while any work item is still awaiting
    // customer approval. Keeps workshop activity from outrunning the approval
    // flow — same gate as the desktop modal. Two signals count as "awaiting":
    // status === 'AWAITING_CONFIRMATION', or confirmationRequired with no
    // customerConfirmed answer yet.
    const hasAwaitingApproval = (job.workItems || []).some(wi =>
        wi.status === 'AWAITING_CONFIRMATION'
        || (wi.confirmationRequired && (wi.customerConfirmed === null || wi.customerConfirmed === undefined))
    );

    const handleStartAll = async () => {
        setBulkBusy(true);
        try { await dispatch(startAllWorkItems({ jobUuid: job.uuid })).unwrap(); }
        catch { /* operationError surfaces via the slice toast */ }
        finally { setBulkBusy(false); setBulkConfirm(null); }
    };
    const handleCompleteAll = async () => {
        setBulkBusy(true);
        try { await dispatch(completeAllWorkItems({ jobUuid: job.uuid })).unwrap(); }
        catch { /* operationError surfaces via the slice toast */ }
        finally { setBulkBusy(false); setBulkConfirm(null); }
    };

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* ── Panel header ── */}
            <div className="shrink-0 px-5 py-4 border-b border-gray-200 bg-white">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-base font-bold text-gray-900 truncate">{job.customerName}</h2>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.pill}`}>{meta.label}</span>
                            {isOverdue && (
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 flex items-center gap-1">
                                    <AlertTriangle size={10} />Overdue
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1"><Car size={11} />{formatVehicleDescription(job.vehicleDescription) || job.vehicleRego}</span>
                            <span className="font-mono text-gray-400">{job.jobNumber}</span>
                            {job.assignedStaffName && (
                                <span className="flex items-center gap-1"><User size={11} />{job.assignedStaffName}</span>
                            )}
                            {activeSeconds > 0 && (
                                <span className={`flex items-center gap-1 font-mono font-semibold
                                    ${isOverdue ? 'text-red-600' : sloStatus === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    <Clock size={11} />{formatTime(activeSeconds / 3600)}
                                    {sloHours && <span className="text-gray-400 font-normal">/ {formatTime(sloHours)}</span>}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Action buttons — persistent, no three-dot menu */}
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                        {job.status === 'IN_PROGRESS' && (
                            <button
                                onClick={() => onPause(job)}
                                disabled={operationLoading}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-xs font-semibold hover:bg-amber-100 transition-colors disabled:opacity-50"
                            >
                                <Pause size={13} /> Pause
                            </button>
                        )}
                        {job.status === 'PAUSED' && (
                            <button
                                onClick={() => onResume(job)}
                                disabled={operationLoading}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-xs font-semibold hover:bg-blue-100 transition-colors disabled:opacity-50"
                            >
                                <Play size={13} /> Resume
                            </button>
                        )}
                        {['CREATED', 'QUOTED', 'APPROVED'].includes(job.status) && (
                            <button
                                onClick={() => onStart(job)}
                                disabled={operationLoading}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                <Play size={13} /> Start job
                            </button>
                        )}
                        {job.status === 'IN_PROGRESS' && (
                            <button
                                onClick={() => onOpenComplete(job)}
                                disabled={operationLoading}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-xl text-xs font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
                            >
                                <CheckCircle size={13} /> Complete
                            </button>
                        )}
                        {job.status === 'COMPLETED' && (
                            <button
                                onClick={() => onOpenVehicleCollect(job)}
                                disabled={operationLoading}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-600 text-white rounded-xl text-xs font-semibold hover:bg-sky-700 transition-colors disabled:opacity-50"
                            >
                                <Car size={13} /> Vehicle collected
                            </button>
                        )}
                        <button
                            onClick={() => onOpenAssign(job)}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-semibold hover:bg-gray-50 transition-colors"
                        >
                            <UserPlus size={13} /> Assign
                        </button>
                        {/* Delete — irreversible. Hits the /purge endpoint and
                            cascades the booking + invoice. Gated by a
                            type-to-confirm dialog rendered at the JobsTabletView
                            level. */}
                        <button
                            onClick={() => onDelete(job)}
                            disabled={operationLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-red-200 text-red-600 rounded-xl text-xs font-semibold hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="Permanently delete this job, its booking and invoice"
                        >
                            <Trash2 size={13} /> Delete
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="shrink-0 flex items-center border-b border-gray-200 bg-white px-5">
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors mr-1
                            ${activeTab === tab.id
                            ? 'border-violet-600 text-violet-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        {tab.label}
                        {tab.dot && (
                            <span
                                className="w-2 h-2 rounded-full bg-amber-500"
                                title="Awaiting customer response"
                            />
                        )}
                        {tab.count > 0 && (
                            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${activeTab === tab.id ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>
                                {tab.count}
                            </span>
                        )}
                    </button>
                ))}

                {/* Bulk Start All / Complete All — only on the Work items tab
                    so the row stays clean on Notes / Approvals / Resources.
                    Counts come from the panel's parent so the buttons render
                    with up-to-date enablement after every job refresh. */}
                {isWorkItemEditable && activeTab === 'workitems' && pendingCount > 0 && !hasAwaitingApproval && (
                    <button
                        onClick={() => setBulkConfirm('start')}
                        disabled={bulkBusy || operationLoading}
                        className="ml-auto my-1.5 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-50 disabled:opacity-50 transition-colors"
                        title={`Start ${pendingCount} pending work item${pendingCount === 1 ? '' : 's'}`}
                    >
                        <Play size={13} /> Start All
                    </button>
                )}
                {isWorkItemEditable && activeTab === 'workitems' && nonTerminalCount > 0 && !hasAwaitingApproval && (
                    <button
                        onClick={() => setBulkConfirm('complete')}
                        disabled={bulkBusy || operationLoading}
                        className={`${pendingCount > 0 ? '' : 'ml-auto'} my-1.5 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-50 disabled:opacity-50 transition-colors`}
                        title={`Complete ${nonTerminalCount} work item${nonTerminalCount === 1 ? '' : 's'} and auto-fill OUT measurements`}
                    >
                        <CheckCheck size={13} /> Complete All
                    </button>
                )}

                {/* Add work item — anchored to the right of the tab row in the
                    same violet primary style as the desktop modal. Stays
                    visible no matter which tab is active so the workshop tech
                    on iPad never has to scroll a long work-item list to find
                    it. Only shown when the job is non-terminal. */}
                {isEditable && (
                    <button
                        onClick={onAddWorkItem}
                        disabled={operationLoading}
                        className={`${(activeTab === 'workitems' && (pendingCount > 0 || nonTerminalCount > 0)) ? '' : 'ml-auto'} my-1.5 flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-medium hover:bg-violet-700 transition-colors disabled:opacity-50`}
                        title="Add a new work item to this job"
                    >
                        <Plus size={13} /> Add Work Item
                    </button>
                )}
            </div>

            {/* ── Tab content ── */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'workitems' && (
                    <WorkItemPanel
                        job={job}
                        dispatch={dispatch}
                        onAddWorkItem={onAddWorkItem}
                        onEditWorkItem={onEditWorkItem}
                        onRemoveWorkItem={onRemoveWorkItem}
                    />
                )}

                {activeTab === 'notes' && (
                    <div className="overflow-y-auto h-full">
                        {/* Notes tab — measurement recording moved to the Work
                            items tab (big IN / OUT buttons on each card), so the
                            Notes tab is now purely note-taking, matching the
                            desktop modal. */}
                        <JobNotesTab
                            job={job}
                            onNoteAdded={onAddNote}
                            onRefreshJob={onRefreshJob}
                            operationLoading={operationLoading}
                        />
                    </div>
                )}

                {/* Customer Approvals tab — mirrors the desktop JobDetailModal.
                    PROFILE-mode jobs get the single job-level JobApprovalCard;
                    non-PROFILE jobs get the BatchApprovalCard which lets the
                    mechanic bundle several ad-hoc items into one request. The
                    ApprovalHistoryList below shows the full audit trail of
                    every request ever sent (with the snapshotted work item
                    names per row) so the workshop has a permanent record. */}
                {activeTab === 'approvals' && (
                    <div className="overflow-y-auto h-full p-5">
                        <div className="space-y-6">
                            {job.approvalMode === 'PROFILE' && (
                                <JobApprovalCard
                                    job={job}
                                    isEditable={isEditable}
                                    operationLoading={operationLoading}
                                    dispatch={dispatch}
                                />
                            )}
                            {job.approvalMode !== 'PROFILE' && (
                                <BatchApprovalCard
                                    job={job}
                                    isEditable={isEditable}
                                    operationLoading={operationLoading}
                                    dispatch={dispatch}
                                />
                            )}
                            <ApprovalHistoryList history={job.approvalHistory} />
                            {/* Empty-state fallback — matches the desktop modal: hidden
                                when history is non-empty, and only relevant for non-
                                PROFILE jobs where the BatchApprovalCard returns null
                                once every work item has been decided and nothing is
                                in flight. */}
                            {job.approvalMode !== 'PROFILE'
                                && (job.workItems || []).every(wi => wi.customerConfirmed === true || wi.customerConfirmed === false)
                                && job.lastApprovalStatus !== 'SENT'
                                && (!Array.isArray(job.approvalHistory) || job.approvalHistory.length === 0)
                                && (
                                    <div className="text-center py-10 text-gray-400 text-sm bg-gray-50 rounded-xl">
                                        <Send size={20} className="mx-auto mb-2 opacity-40" />
                                        No items awaiting customer approval
                                    </div>
                                )}
                        </div>
                    </div>
                )}

                {activeTab === 'resources' && (
                    <div className="overflow-y-auto h-full p-5">
                        <ResourcesTabletPanel
                            jobResources={jobResources}
                            availableResources={availableResources}
                            resourcesLoading={resourcesLoading}
                            operationLoading={operationLoading}
                            resourceTypeFilter={resourceTypeFilter}
                            onFilterChange={onResourceTypeFilter}
                            onAssign={onAssignResource}
                            onUnassign={onUnassignResource}
                        />
                    </div>
                )}
            </div>

            {/* Bulk Start All / Complete All confirmation. Rendered at the end
                so it sits on top of the panel content via its own fixed backdrop.
                Same component as work-item-removal confirms — variant 'primary'
                because these aren't destructive in the same sense as a delete. */}
            <TabletConfirmDialog
                open={bulkConfirm != null}
                title={bulkConfirm === 'complete' ? 'Complete all work items?' : 'Start all work items?'}
                message={
                    bulkConfirm === 'complete'
                        ? `This will mark ${nonTerminalCount} work item${nonTerminalCount === 1 ? '' : 's'} as completed and auto-fill every OUT measurement with "OK". Items already completed, declined, or awaiting customer confirmation will be skipped.`
                        : `This will move ${pendingCount} pending work item${pendingCount === 1 ? '' : 's'} into In Progress.`
                }
                confirmLabel={bulkConfirm === 'complete' ? 'Complete All' : 'Start All'}
                cancelLabel="Cancel"
                variant="primary"
                onConfirm={bulkConfirm === 'complete' ? handleCompleteAll : handleStartAll}
                onCancel={() => setBulkConfirm(null)}
            />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty state for right panel when no job is selected
// ─────────────────────────────────────────────────────────────────────────────

const NothingSelected = () => (
    <div className="flex flex-col items-center justify-center h-full text-gray-300 gap-4">
        <LayoutPanelLeft size={48} strokeWidth={1} />
        <p className="text-sm text-gray-400">Select a job from the list to view its details</p>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Column filter pill
// ─────────────────────────────────────────────────────────────────────────────

const COLUMN_FILTERS = [
    { id: 'all',         label: 'All jobs',    statuses: null },
    { id: 'queued',      label: 'Queued',       statuses: QUEUED_COLUMN_STATUSES },
    { id: 'in-progress', label: 'In progress',  statuses: ['IN_PROGRESS'] },
    { id: 'paused',      label: 'Paused',       statuses: ['PAUSED'] },
    { id: 'completed',   label: 'Completed',    statuses: ['COMPLETED'] },
];

// ─────────────────────────────────────────────────────────────────────────────
// JobsTabletView — the main export
// ─────────────────────────────────────────────────────────────────────────────

const JobsTabletView = ({ onSwitchToKanban, onSwitchToPlanner }) => {
    const dispatch = useDispatch();

    const {
        jobs,
        selectedJob,
        jobsLoading,
        operationLoading,
        operationError,
        operationSuccess,
        showAssignModal,
        showCancelConfirm,
        showCompleteConfirm,
        showVehicleCollectedConfirm,
        jobResources,
        availableResources,
        resourcesLoading,
        workCategories,
        categoryTypes,
    } = useSelector(state => state.jobs);

    const { staffList, isLoading: staffLoading } = useSelector(state => state.staff);

    const [currentTime, setCurrentTime]         = useState(new Date());
    const [searchQuery, setSearchQuery]         = useState('');
    const [columnFilter, setColumnFilter]       = useState('all');
    const [cancelReason, setCancelReason]       = useState('');
    const [odometerOut, setOdometerOut]         = useState('');
    const [staffSearch, setStaffSearch]         = useState('');
    const [resourceTypeFilter, setResourceTypeFilter] = useState('');
    const [addWorkItemForJob, setAddWorkItemForJob]   = useState(null);
    const [editWorkItemData, setEditWorkItemData]     = useState(null); // { job, workItem }

    // Delete-job confirm dialog state — same type-to-confirm gate as the
    // desktop modal, kept local so it doesn't pollute the slice with a
    // per-view UI flag.
    const [deleteJobTarget, setDeleteJobTarget] = useState(null);
    const [deleteJobInput,  setDeleteJobInput]  = useState('');

    const [loanCarReturnJob, setLoanCarReturnJob]           = useState(null);
    const [loanCarHandoverStatus, setLoanCarHandoverStatus] = useState(null);
    const [loanCarCheckLoading, setLoanCarCheckLoading]     = useState(false);

    // "My jobs only" toggle — when true, asks the backend to restrict the
    // returned jobs to those assigned to the currently logged-in user.
    const [myJobsOnly, setMyJobsOnly] = useState(false);

    // Date filter — the tablet view is restricted to jobs scheduled within a
    // single calendar month. Defaults to the current month; user can step
    // backward/forward or pick any month via the date input. The window is
    // always exactly one month — backend rejects ranges > 31 days.
    const [selectedMonth, setSelectedMonth] = useState(currentMonthKey);

    // 🔑 The queued jobs come from their own page of a hundred, never from the month
    // payload — the same rule as the board and the planner. The toolbar's search box
    // narrows that page locally by name or rego; the server is never asked.
    // The box is the toolbar's, so only the server half is taken here: the text
    // narrows both lists on the keystroke, the settled term asks for the queued
    // page the search is actually about. See useServerSearchTerm.
    const queuedSearch = useServerSearchTerm(searchQuery);
    const queuedJobs = useQueuedJobs({ q: queuedSearch.q });

    // Clock tick
    useEffect(() => {
        const t = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // Initial data fetch + refetch when "my jobs only" toggles.
    // The jobs list is refetched every time myJobsOnly changes (including the
    // initial false->false mount fire), so we don't need a separate mount
    // effect for it — keeping them separate caused fetchJobs to fire twice on
    // mount, which made the refresh button appear dead because a refresh was
    // always racing an in-flight duplicate request.
    useEffect(() => {
        dispatch(fetchJobSummary());
        dispatch(fetchWorkCategories());
        dispatch(fetchCategoryTypes());
    }, [dispatch]);

    useEffect(() => {
        // Same query as the kanban board — see utils/jobsBoardQuery.js.
        dispatch(fetchJobs(jobsBoardQuery({ selectedMonth, myJobsOnly })));
    }, [myJobsOnly, selectedMonth, dispatch]);

    // Fetch job detail whenever selected job changes
    useEffect(() => {
        if (selectedJob?.uuid) {
            dispatch(fetchJob(selectedJob.uuid));
            dispatch(fetchJobResources(selectedJob.uuid));
            dispatch(fetchAvailableResources({ jobUuid: selectedJob.uuid }));
            setResourceTypeFilter('');
        }
    }, [selectedJob?.uuid, dispatch]);

    useEffect(() => {
        if (selectedJob?.uuid) {
            dispatch(fetchAvailableResources({ jobUuid: selectedJob.uuid, type: resourceTypeFilter || null }));
        }
    }, [resourceTypeFilter]);

    useEffect(() => {
        if (showAssignModal) {
            setStaffSearch('');
            dispatch(fetchStaffList({ activeOnly: true, size: 100 }));
        }
    }, [showAssignModal, dispatch]);

    const reloadQueued = queuedJobs.reload;
    const handleRefresh = useCallback(() => {
        dispatch(fetchJobs(jobsBoardQuery({ selectedMonth, myJobsOnly, bustCache: true })));
        reloadQueued();
        dispatch(fetchJobSummary());
    }, [dispatch, myJobsOnly, selectedMonth, reloadQueued]);

    // Job selection — fires fetchJob for full detail
    const handleSelectJob = (job) => {
        dispatch(selectJob(job));
    };

    // Action handlers (same as JobsPage, no logic change)
    const handleStartJob  = (job) => dispatch(startJob(job.uuid));
    const handlePauseJob  = (job) => dispatch(pauseJob({ uuid: job.uuid, reason: null }));
    const handleResumeJob = (job) => dispatch(resumeJob(job.uuid));

    const handleCompleteJob = () => {
        if (!selectedJob || !odometerOut) return;
        dispatch(completeJob({ uuid: selectedJob.uuid, odometerOut: parseInt(odometerOut) }));
        setOdometerOut('');
    };

    const handleOpenVehicleCollect = useCallback(async (job) => {
        dispatch(selectJob(job));
        setLoanCarCheckLoading(true);
        try {
            const status = await getLoanCarHandoverStatusApi(job.uuid);
            if (status.hasOut && !status.hasIn) {
                setLoanCarHandoverStatus(status);
                setLoanCarReturnJob(job);
            } else {
                setLoanCarHandoverStatus(null);
                setLoanCarReturnJob(null);
                dispatch(openVehicleCollectedConfirm(job));
            }
        } catch {
            setLoanCarHandoverStatus(null);
            setLoanCarReturnJob(null);
            dispatch(openVehicleCollectedConfirm(job));
        } finally {
            setLoanCarCheckLoading(false);
        }
    }, [dispatch]);

    const handleLoanCarReturnDone = useCallback(({ recorded }) => {
        const job = loanCarReturnJob;
        setLoanCarReturnJob(null);
        setLoanCarHandoverStatus(null);
        if (recorded) dispatch(setOperationSuccess('Loan car return recorded'));
        dispatch(openVehicleCollectedConfirm(job));
    }, [dispatch, loanCarReturnJob]);

    const handleVehicleCollected = () => {
        if (selectedJob) dispatch(vehicleCollected({ uuid: selectedJob.uuid }));
    };

    const handleCancelJob = () => {
        if (selectedJob) {
            dispatch(cancelJob({ uuid: selectedJob.uuid, reason: cancelReason || null }));
            setCancelReason('');
        }
    };

    const openDeleteDialog = (job) => {
        setDeleteJobTarget(job);
        setDeleteJobInput('');
    };
    const closeDeleteDialog = () => {
        setDeleteJobTarget(null);
        setDeleteJobInput('');
    };
    const handleConfirmDeleteJob = () => {
        if (!deleteJobTarget) return;
        if (deleteJobInput.trim() !== deleteJobTarget.jobNumber) return;
        // purgeJob.fulfilled clears selectedJob and removes the row from
        // state.jobs, so the right-hand detail panel collapses automatically.
        dispatch(purgeJob(deleteJobTarget.uuid));
        closeDeleteDialog();
    };

    const handleAddNoteFromTab = async ({ content, noteType, customerVisible, workItemUuid }) => {
        if (!selectedJob) return null;
        const result = await dispatch(addJobNote({
            jobUuid: selectedJob.uuid,
            data: { content, noteType: noteType || 'PROGRESS_UPDATE', customerVisible: !!customerVisible, workItemUuid: workItemUuid || null }
        }));
        if (addJobNote.fulfilled.match(result)) return result.payload;
        throw new Error(result.payload || 'Failed to add note');
    };

    const handleRefreshJob = async () => {
        if (!selectedJob) return null;
        const result = await dispatch(fetchJob(selectedJob.uuid));
        if (fetchJob.fulfilled.match(result)) return result.payload;
        return null;
    };

    const handleAddWorkItem  = (payload) => { if (selectedJob) dispatch(addWorkItem({ jobUuid: selectedJob.uuid, data: payload })); };
    const handleEditWorkItem = (payload, workItemUuid) => { if (selectedJob && workItemUuid) dispatch(updateWorkItem({ jobUuid: selectedJob.uuid, workItemUuid, data: payload })); };
    const handleRemoveWorkItem = (workItemUuid) => { if (selectedJob) dispatch(removeWorkItem({ jobUuid: selectedJob.uuid, workItemUuid })); };

    const handleAssignStaff = (staffMember) => {
        if (selectedJob) dispatch(assignStaff({ uuid: selectedJob.uuid, staffUuid: staffMember.uuid }));
    };
    const handleAssignResource   = (r) => { const id = r.uuid || r.resourceUuid; if (selectedJob && id) dispatch(assignResource({ jobUuid: selectedJob.uuid, data: { resourceUuid: id } })); };
    const handleUnassignResource = (r) => { const id = r.resourceUuid || r.uuid; if (selectedJob && id) dispatch(unassignResource({ jobUuid: selectedJob.uuid, resourceUuid: id })); };

    // ── Filtered job list ──
    const activeFilter = COLUMN_FILTERS.find(f => f.id === columnFilter) || COLUMN_FILTERS[0];
    // Queued first, from its own page; then the month's finished work. A CREATED or
    // QUOTED job in the month payload is skipped — it is on the queued page already.
    const showQueued = !activeFilter.statuses || activeFilter.statuses.some(isQueuedStatus);
    const queuedVisible = showQueued ? filterByNameOrRego(queuedJobs.jobs, searchQuery) : [];
    const monthJobs = jobs
        .filter(j => !isQueuedStatus(j.status))
        .filter(j => !activeFilter.statuses || activeFilter.statuses.includes(j.status))
        .filter(j => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            return (
                j.jobNumber?.toLowerCase().includes(q) ||
                j.customerName?.toLowerCase().includes(q) ||
                j.vehicleDescription?.toLowerCase().includes(q) ||
                j.vehicleRego?.toLowerCase().includes(q)
            );
        });
    const filteredJobs = [...queuedVisible, ...monthJobs];
    // The sidebar waits on whichever source it is drawing from. "No jobs found" is
    // only said once both have answered.
    const sidebarLoading = jobsLoading || (showQueued && queuedJobs.loading);

    const overdueCount = jobs
        .filter(j => j.status === 'IN_PROGRESS')
        .filter(j => j.overdue === true || getSloStatus(getLiveActiveSeconds(j), j.sloBudgetSeconds) === 'red')
        .length;

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <Layout>
            <div className="flex flex-col h-full overflow-hidden bg-gray-100">

                {/* ── Toasts ── */}
                {operationSuccess && (
                    <Toast message={operationSuccess} type="success" onDismiss={() => dispatch(clearOperationStatus())} />
                )}
                {operationError && !showCompleteConfirm && (
                    <Toast message={operationError} type="error" onDismiss={() => dispatch(clearOperationStatus())} />
                )}

                {/* Loan car check overlay */}
                {loanCarCheckLoading && (
                    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[55]">
                        <div className="bg-white rounded-2xl px-6 py-5 flex items-center gap-3 shadow-xl">
                            <RefreshCw size={18} className="animate-spin text-sky-600" />
                            <span className="text-sm font-medium text-gray-700">Checking loan car status…</span>
                        </div>
                    </div>
                )}

                {/* ── Shared toolbar — identical structure to Kanban / Planner. ──
                    Column-filter pills live in the title-extras slot so the toolbar
                    height matches the other views (no second header row). */}
                <JobsToolbar
                    activeView="tablet"
                    onSwitchView={(id) => {
                        if (id === 'tablet')  return;
                        if (id === 'kanban'  && onSwitchToKanban)  onSwitchToKanban();
                        if (id === 'planner' && onSwitchToPlanner) onSwitchToPlanner();
                    }}
                    title="Jobs Board"
                    subtitle={`${currentTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} · ${jobs.filter(j => !isQueuedStatus(j.status)).length + queuedJobs.total} active${overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}`}
                    titleExtras={
                        <>
                            {COLUMN_FILTERS.map((f) => {
                                const isActive = columnFilter === f.id;
                                // Queued is the server's count of ALL of them, not the page.
                                const count = f.id === 'queued'
                                    ? queuedJobs.total
                                    : f.statuses
                                        ? jobs.filter(j => f.statuses.includes(j.status)).length
                                        : null;
                                return (
                                    <button
                                        key={f.id}
                                        type="button"
                                        onClick={() => setColumnFilter(f.id)}
                                        className={`shrink-0 h-7 text-[11px] font-semibold px-2.5 rounded-full transition-colors border
                                            ${isActive
                                            ? 'bg-violet-600 text-white border-violet-600'
                                            : 'bg-white text-gray-500 border-gray-200 hover:border-violet-300 hover:text-violet-600'}`}
                                    >
                                        {f.label}
                                        {count != null && (
                                            <span className="ml-1 opacity-75">{count}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </>
                    }
                    selectedMonth={selectedMonth}
                    setSelectedMonth={setSelectedMonth}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    myJobsOnly={myJobsOnly}
                    setMyJobsOnly={setMyJobsOnly}
                    onRefresh={handleRefresh}
                    refreshing={jobsLoading}
                />

                {/* ── Two-panel split ── */}
                <div className="flex flex-1 min-h-0">

                    {/* Left: job list sidebar */}
                    <div className="w-72 shrink-0 flex flex-col border-r border-gray-200 bg-white overflow-hidden">
                        <div className="shrink-0 px-3 py-2 border-b border-gray-100 space-y-1.5">
                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
                            </span>
                            {/* The queued page's pager. The search box is the toolbar's. */}
                            {showQueued && (
                                <PagedSearchControls
                                    showSearch={false}
                                    alwaysShowPager
                                    page={queuedJobs.page}
                                    totalPages={queuedJobs.totalPages}
                                    total={queuedJobs.total}
                                    size={queuedJobs.size}
                                    onPage={queuedJobs.setPage}
                                    pagerLabel="queued jobs"
                                    error={queuedSearch.error}
                                />
                            )}
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {sidebarLoading ? (
                                <LaneLoading className="p-3" />
                            ) : filteredJobs.length === 0 ? (
                                <div className="text-center py-12 text-gray-400 text-sm">No jobs found</div>
                            ) : (
                                filteredJobs.map((job) => (
                                    <SidebarJobRow
                                        key={job.uuid}
                                        job={job}
                                        isSelected={selectedJob?.uuid === job.uuid}
                                        onClick={() => handleSelectJob(job)}
                                        currentTime={currentTime}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* Right: job detail panel */}
                    <div className="flex-1 min-w-0 bg-gray-50 overflow-hidden">
                        {selectedJob ? (
                            <JobDetailPanel
                                job={selectedJob}
                                dispatch={dispatch}
                                operationLoading={operationLoading}
                                jobResources={jobResources}
                                availableResources={availableResources}
                                resourcesLoading={resourcesLoading}
                                workCategories={workCategories}
                                categoryTypes={categoryTypes}
                                onStart={handleStartJob}
                                onPause={handlePauseJob}
                                onResume={handleResumeJob}
                                onOpenComplete={(j) => { dispatch(selectJob(j)); dispatch(openCompleteConfirm(j)); }}
                                onOpenVehicleCollect={handleOpenVehicleCollect}
                                onOpenAssign={(j) => { dispatch(selectJob(j)); dispatch(openAssignModal(j)); }}
                                onAddWorkItem={() => setAddWorkItemForJob(selectedJob)}
                                onEditWorkItem={(wi) => setEditWorkItemData({ job: selectedJob, workItem: wi })}
                                onRemoveWorkItem={handleRemoveWorkItem}
                                onAddNote={handleAddNoteFromTab}
                                onRefreshJob={handleRefreshJob}
                                onAssignResource={handleAssignResource}
                                onUnassignResource={handleUnassignResource}
                                resourceTypeFilter={resourceTypeFilter}
                                onResourceTypeFilter={setResourceTypeFilter}
                                onDelete={openDeleteDialog}
                            />
                        ) : (
                            <NothingSelected />
                        )}
                    </div>
                </div>

                {/* ─────────────────────────────────────────────────────────────
                    Modals — same structure as JobsPage, no logic changed
                ───────────────────────────────────────────────────────────── */}

                {/* Add / Edit Work Item Modal */}
                {addWorkItemForJob && (
                    <AddWorkItemModal
                        job={addWorkItemForJob}
                        mode="add"
                        workCategories={workCategories}
                        categoryTypes={categoryTypes}
                        loading={operationLoading}
                        error={operationError}
                        onClose={() => setAddWorkItemForJob(null)}
                        onSubmit={(payload) => {
                            handleAddWorkItem(payload);
                            setAddWorkItemForJob(null);
                        }}
                    />
                )}
                {editWorkItemData && (
                    <AddWorkItemModal
                        job={editWorkItemData.job}
                        mode="edit"
                        initialValues={editWorkItemData.workItem}
                        workCategories={workCategories}
                        categoryTypes={categoryTypes}
                        loading={operationLoading}
                        error={operationError}
                        onClose={() => setEditWorkItemData(null)}
                        onSubmit={(payload) => {
                            handleEditWorkItem(payload, editWorkItemData.workItem.uuid);
                            setEditWorkItemData(null);
                        }}
                    />
                )}

                {/* Assign Staff Modal */}
                {showAssignModal && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl w-full max-w-md flex flex-col max-h-[80vh] shadow-2xl">
                            <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Assign Staff</h2>
                                    <p className="text-sm text-gray-500 mt-0.5">
                                        <span className="font-medium text-gray-700">{selectedJob.jobNumber}</span>
                                        {selectedJob.assignedStaffName && (
                                            <span className="ml-1">· currently <span className="text-violet-600">{selectedJob.assignedStaffName}</span></span>
                                        )}
                                    </p>
                                </div>
                                <button onClick={() => dispatch(closeAssignModal())} className="p-2 hover:bg-gray-100 rounded-xl"><X size={20} /></button>
                            </div>
                            <div className="px-4 py-3 border-b shrink-0">
                                <div className="relative">
                                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search by name or role…"
                                        value={staffSearch}
                                        onChange={e => setStaffSearch(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <div className="overflow-y-auto flex-1 p-3">
                                {staffLoading ? (
                                    <div className="flex items-center justify-center gap-2 py-10 text-gray-400 text-sm">
                                        <RefreshCw size={15} className="animate-spin" /> Loading staff…
                                    </div>
                                ) : (() => {
                                    const filtered = (staffList || []).filter(s =>
                                        !staffSearch ||
                                        s.fullName?.toLowerCase().includes(staffSearch.toLowerCase()) ||
                                        s.role?.toLowerCase().includes(staffSearch.toLowerCase())
                                    );
                                    if (!filtered.length) return <div className="text-center py-10 text-gray-400 text-sm">No staff found</div>;
                                    return (
                                        <div className="space-y-1">
                                            {filtered.map((staff) => {
                                                const isAssigned = selectedJob.assignedStaffUuid === staff.uuid;
                                                const initials   = `${staff.firstName?.[0] || ''}${staff.lastName?.[0] || ''}`.toUpperCase() || '?';
                                                return (
                                                    <button
                                                        key={staff.uuid}
                                                        onClick={() => handleAssignStaff(staff)}
                                                        disabled={operationLoading || isAssigned}
                                                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors border-2
                                                            ${isAssigned ? 'bg-violet-50 border-violet-300 cursor-default' : 'border-transparent hover:bg-gray-50 hover:border-gray-200'}`}
                                                    >
                                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${isAssigned ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{initials}</div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">{staff.fullName}</p>
                                                            <p className="text-xs text-gray-500 truncate">{staff.role?.replace(/_/g, ' ')}</p>
                                                        </div>
                                                        {isAssigned && <span className="text-xs text-violet-600 font-medium shrink-0">Assigned</span>}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                            {selectedJob.assignedStaffUuid && (
                                <div className="px-4 py-3 border-t shrink-0">
                                    <button
                                        onClick={() => dispatch(unassignStaff(selectedJob.uuid))}
                                        disabled={operationLoading}
                                        className="w-full py-2 text-sm text-red-500 hover:bg-red-50 rounded-xl border border-red-200 hover:border-red-300 transition-colors"
                                    >
                                        Remove Assignment
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Complete Job Modal */}
                {showCompleteConfirm && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl w-full max-w-md m-4">
                            <div className="p-6 border-b flex items-center gap-3">
                                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                                    <CheckCircle size={18} className="text-emerald-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Complete Job</h2>
                                    <p className="text-sm text-gray-500 mt-0.5">Mark {selectedJob.jobNumber} as completed?</p>
                                </div>
                            </div>
                            <div className="p-6 space-y-4">
                                {operationError && (
                                    <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                        <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
                                        <p className="text-sm text-red-700 leading-snug">{operationError}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Odometer Out <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            value={odometerOut}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const num = parseInt(val);
                                                setOdometerOut(isNaN(num) || num < 0 ? '' : String(num));
                                            }}
                                            placeholder="e.g. 85000"
                                            min="0"
                                            max="9999999"
                                            className={`w-full border rounded-lg p-3 pr-10 focus:outline-none focus:ring-2 focus:ring-emerald-500
                                                [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                                                ${!odometerOut ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">km</span>
                                    </div>
                                    {!odometerOut && <p className="mt-1 text-xs text-red-500">Required — record the odometer reading when returning the vehicle</p>}
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => { dispatch(closeCompleteConfirm()); setOdometerOut(''); }} className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                                    <button onClick={handleCompleteJob} disabled={operationLoading || !odometerOut} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium">
                                        {operationLoading ? 'Completing…' : 'Complete Job'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Loan Car Return Modal */}
                {loanCarReturnJob && loanCarHandoverStatus && (
                    <LoanCarReturnModal
                        jobUuid={loanCarReturnJob.uuid}
                        handoverStatus={loanCarHandoverStatus}
                        onDone={handleLoanCarReturnDone}
                    />
                )}

                {/* Vehicle Collected Confirm */}
                {showVehicleCollectedConfirm && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl w-full max-w-md m-4">
                            <div className="p-6 border-b">
                                <div className="flex items-center gap-3 mb-1">
                                    <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                                        <Car size={18} className="text-sky-600" />
                                    </div>
                                    <h2 className="text-lg font-bold text-gray-900">Vehicle Collected</h2>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                    Confirm that the customer has collected their vehicle for{' '}
                                    <span className="font-semibold text-gray-700">{selectedJob.jobNumber}</span>?
                                </p>
                                <p className="text-xs text-gray-400 mt-2">This will remove the job from the board and mark the booking as completed.</p>
                            </div>
                            <div className="p-6 flex gap-2">
                                <button onClick={() => dispatch(closeVehicleCollectedConfirm())} className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                                <button onClick={handleVehicleCollected} disabled={operationLoading} className="flex-1 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 text-sm font-medium">
                                    {operationLoading ? 'Processing…' : 'Confirm Collection'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Cancel Confirm Modal */}
                {showCancelConfirm && selectedJob && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-2xl w-full max-w-md m-4">
                            <div className="p-6 border-b">
                                <h2 className="text-lg font-bold text-gray-900">Cancel Job</h2>
                                <p className="text-sm text-gray-500 mt-1">Are you sure you want to cancel {selectedJob.jobNumber}?</p>
                            </div>
                            <div className="p-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Reason (optional)</label>
                                <textarea
                                    value={cancelReason}
                                    onChange={e => setCancelReason(e.target.value)}
                                    placeholder="Enter cancellation reason…"
                                    className="w-full border rounded-lg p-3 h-24 resize-none focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                                <div className="flex gap-2 mt-4">
                                    <button onClick={() => dispatch(closeCancelConfirm())} className="flex-1 py-2 border rounded-lg hover:bg-gray-50">Keep Job</button>
                                    <button onClick={handleCancelJob} disabled={operationLoading} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                                        {operationLoading ? 'Cancelling…' : 'Cancel Job'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete-job type-to-confirm dialog.
                    Backed by DELETE /api/jobs/{uuid}/purge — irreversibly
                    removes the job and cascades through its booking and
                    invoice. The user must type the exact job number for
                    the destructive button to enable. */}
                {deleteJobTarget && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
                        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
                            <div className="px-6 py-4 border-b flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                                    <AlertTriangle size={20} className="text-red-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Delete job?</h2>
                                    <p className="text-xs text-gray-500 mt-0.5">This action is permanent and cannot be undone.</p>
                                </div>
                            </div>
                            <div className="px-6 py-5 space-y-4">
                                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 space-y-1">
                                    <p className="font-semibold">The following will be permanently deleted:</p>
                                    <ul className="list-disc pl-5 space-y-0.5">
                                        <li>Job <span className="font-mono">{deleteJobTarget.jobNumber}</span> and all its work items, notes and observations</li>
                                        <li>Its booking (if any)</li>
                                        <li>Its invoice and line items (if any)</li>
                                    </ul>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Type <span className="font-mono font-semibold text-gray-900">{deleteJobTarget.jobNumber}</span> to confirm
                                    </label>
                                    <input
                                        type="text"
                                        value={deleteJobInput}
                                        onChange={(e) => setDeleteJobInput(e.target.value)}
                                        placeholder={deleteJobTarget.jobNumber}
                                        className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                                        autoFocus
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={closeDeleteDialog}
                                        className="flex-1 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmDeleteJob}
                                        disabled={operationLoading || deleteJobInput.trim() !== deleteJobTarget.jobNumber}
                                        className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold"
                                    >
                                        {operationLoading ? 'Deleting…' : 'Delete job'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
};

// Re-export useIsTablet for back-compat with callers that already import it
// from this file. The hook itself lives in ./useIsTablet — keeping it there
// is what makes Vite Fast Refresh happy (this file becomes a pure-components
// module; re-export statements don't count against that rule).
export { useIsTablet } from './useIsTablet';
export default JobsTabletView;